"use client";

import { createContext, useContext, type ReactNode } from "react";
import { DEFAULT_LOCALE, localizedHref, type Locale } from "../const/i18n";
import type { UiMessages } from "../dictionaries/types";

type I18nValue = { lang: Locale; messages: Partial<UiMessages> };

const I18nContext = createContext<I18nValue>({
  lang: DEFAULT_LOCALE,
  messages: {},
});

// Mounted by app/[lang]/layout.tsx, with the locale's text for the components
// shared with the English pages. English pages and the app have no provider, so
// no other language reaches their bundles.
export function I18nProvider({
  lang,
  messages,
  children,
}: I18nValue & { children: ReactNode }) {
  return (
    <I18nContext.Provider value={{ lang, messages }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useLocale = (): Locale => useContext(I18nContext).lang;

// A shared component's text. It passes its English namespace, so each bundle only
// carries the English of the components it uses.
export function useMessages<N extends keyof UiMessages>(
  namespace: N,
  english: UiMessages[N],
): UiMessages[N] {
  return (useContext(I18nContext).messages[namespace] as UiMessages[N]) ?? english;
}

export const useLocalizedHref = (): ((path: string) => string) => {
  const lang = useLocale();
  return (path) => localizedHref(lang, path);
};
